import { MedplumClient, PatchOperation } from "@medplum/core";
import {
  Bundle,
  BundleEntry,
  ChargeItem,
  Encounter,
  Parameters,
  Period,
  Procedure,
} from "@medplum/fhirtypes";

import { buildChargeItemStatusPatch } from "@/billing/components/ServicesTab/utils/charge-item-status-tag";
import { ChargeItemProvenanceActivityCode } from "@/billing/constants/charge-item-provenance";
import { loadClientBillingPaymentMethod } from "@/billing/services/client-billing-payment-method";
import { afterChargeItemWrites } from "@/billing/services/record-charge-item-provenance";
import { CapturedPlaceOfService } from "@/billing/types/encounter-place-of-service";
import { replacePlaceOfServiceExtension } from "@/billing/utils/build-charge-item-payload";
import {
  refreshInferredPlaceOfService,
  resolveCapturedPlaceOfService,
} from "@/billing/utils/encounter-place-of-service";
import { PaymentMethod } from "@/constants/billing";
import {
  resourceHasOrganizationCompartment,
  resourceHasOrganizationLinkage,
  setAccountsBestEffort,
} from "@/utils/accounts";
import { toFailureDetails } from "@/utils/errors";
import { joinReferenceString, resourceIdFromLocation } from "@/utils/medplum";

import {
  CaptureEncounterChargeItemsReadFailureReason,
  CaptureEncounterChargeItemsResult,
  CaptureEncounterChargeItemsSuccess,
  UpdateEncounterChargeItemsLocationResult,
  VoidEncounterChargeItemResult,
} from "../types/encounter-charge-items";
import {
  buildEncounterChargeItems,
  CAPTURED_CHARGE_ITEM_IDENTIFIER_SYSTEM,
  CapturedChargeItem,
  getActiveChargeItems,
  getCapturedChargeItemKey,
  resolveEncounterOccurrencePeriod,
  VOIDED_CHARGE_ITEM_STATUS,
} from "../utils/encounter-charge-items";
import { resolveEncounterClinicalProviderId } from "../utils/encounter-participants";

import { searchEncounterChargeItems } from "./encounter-charge-items-search";
import { tagCapturedServicesStatus } from "./encounter-services-status-tag";

type CaptureEncounterChargeItemsParams = {
  medplum: MedplumClient;
  clientId: string;
  encounterId: string;
  /** The encounter's live procedures, read after the encounter's own save has written them. */
  procedures: Procedure[];
  /**
   * The practice whose fee definition the charge is priced against.
   *
   * The caller passes it in instead of this service reading it. It comes from the active practice
   * route or the signed-in session, and a service that reads it from browser storage could only run
   * in a browser and could only be tested in one. Passing `undefined` is a choice the caller makes:
   * it points the charge at the global fee definition instead of the practice's.
   */
  practiceOrganizationId: string | undefined;
};

/** Everything the builder needs about the encounter that has to be read off the server first. */
type EncounterChargeContext = {
  /** The encounter itself, whose compartment decides which practice its charges are linked to. */
  encounter: Encounter;
  existingChargeItems: ChargeItem[];
  occurrencePeriod: Period | undefined;
  paymentMethod: PaymentMethod;
  /** Clinical provider stamped onto each captured ChargeItem.performer. */
  performerPractitionerId: string | undefined;
  /**
   * The Location to stamp on new charges and where it came from. See
   * {@link resolveCapturedPlaceOfService} for the order.
   */
  placeOfService: CapturedPlaceOfService | undefined;
};

/** Which practice to link charges to, checked against the encounter they were captured on. */
type PracticeLinkage = {
  encounter: Encounter;
  /** Already trimmed. Linkage is skipped without one. */
  practiceOrganizationId: string | undefined;
};

type ReadEncounterChargeContextResult =
  | { ok: true; data: EncounterChargeContext }
  | { ok: false; reason: CaptureEncounterChargeItemsReadFailureReason; details?: string };

/** A conditional create the server carried out. */
const CREATED_STATUS = "201";

/** A conditional create that matched a charge already there, so it wrote nothing. */
const MATCHED_STATUS = "200";

const toStatusCode = (entry: BundleEntry | undefined): string | undefined =>
  entry?.response?.status?.split(" ")[0];

/**
 * Prefers an embedded ChargeItem that already has an id. When the server omits the id, reads the
 * saved copy from `response.location`. Returns undefined when neither source is usable.
 */
const savedChargeItem = async (
  medplum: MedplumClient,
  entry: BundleEntry | undefined,
): Promise<ChargeItem | undefined> => {
  const resource = entry?.resource;
  if (resource?.resourceType === "ChargeItem" && resource.id) return resource;

  const resourceId = resourceIdFromLocation(entry?.response?.location, "ChargeItem");
  if (!resourceId) return undefined;

  return medplum.readResource("ChargeItem", resourceId);
};

/**
 * The ChargeItems a transaction's Location PATCH entries wrote, for Version History.
 *
 * Same resolver as capture: the patched ChargeItem, or a read from `response.location` when the
 * PATCH entry is Parameters. A missing or failed read leaves a Version History gap rather than
 * failing a write that has already committed.
 *
 * @param medplum Client that reads a charge back from its location.
 * @param entries The response entries for the PATCHes, in request order.
 * @param count How many PATCHes were sent.
 * @returns The patched charges that could be resolved.
 */
const resolvePatchedChargeItems = async (
  medplum: MedplumClient,
  entries: BundleEntry[],
  count: number,
): Promise<ChargeItem[]> =>
  (
    await Promise.all(
      Array.from({ length: count }, (_, index) =>
        savedChargeItem(medplum, entries[index]).catch(() => undefined),
      ),
    )
  ).filter((chargeItem): chargeItem is ChargeItem => chargeItem !== undefined);

/**
 * Reads the encounter, what it has already been charged for, and who is paying.
 *
 * Fails if any of the three reads fails, each under its own reason. All three are needed: a charge
 * built on a missing encounter would carry no date, one built without knowing what already exists
 * would charge twice, and one built without knowing the payer would name a payer nobody chose.
 *
 * On success, `data` holds the charges already on the encounter, the period to record a charge
 * over, the payment method to stamp on it, and the Location to stamp on it.
 */
const readEncounterChargeContext = async (
  medplum: MedplumClient,
  clientId: string,
  encounterId: string,
): Promise<ReadEncounterChargeContextResult> => {
  // None of the three reads depends on the others, so they all go out together. `allSettled` rather
  // than `all`, so a failure is reported as the read that actually failed.
  const [existingChargeItems, encounter, paymentMethod] = await Promise.allSettled([
    searchEncounterChargeItems(medplum, encounterId),
    medplum.readResource("Encounter", encounterId),
    loadClientBillingPaymentMethod(medplum, clientId),
  ]);

  if (encounter.status === "rejected") {
    return {
      ok: false,
      reason: "encounter_read_failed",
      details: toFailureDetails(encounter.reason),
    };
  }

  if (existingChargeItems.status === "rejected") {
    return {
      ok: false,
      reason: "existing_charges_read_failed",
      details: toFailureDetails(existingChargeItems.reason),
    };
  }

  if (paymentMethod.status === "rejected") {
    return {
      ok: false,
      reason: "payment_method_read_failed",
      details: toFailureDetails(paymentMethod.reason),
    };
  }

  const resolvedPaymentMethod = paymentMethod.value;

  // `undetermined` is an answer: intake is where the client says how they expect to pay, and a client
  // who never answered is self-pay — the one payer that needs no coverage behind it. `load_failed` is
  // not an answer. Falling back there would put a payer on a billing record on the strength of a read
  // that did not happen, so the capture fails instead and can be retried.
  if (!resolvedPaymentMethod.ok && resolvedPaymentMethod.reason === "load_failed") {
    return {
      ok: false,
      reason: "payment_method_read_failed",
      details: resolvedPaymentMethod.details,
    };
  }

  const clinicalProviderId = resolveEncounterClinicalProviderId(encounter.value);

  return {
    ok: true,
    data: {
      encounter: encounter.value,
      // Voided charges count, so a procedure whose charge a biller deleted is not charged for again —
      // see toConditionalCreateEntry.
      existingChargeItems: existingChargeItems.value,
      occurrencePeriod: resolveEncounterOccurrencePeriod(encounter.value),
      paymentMethod: resolvedPaymentMethod.ok
        ? resolvedPaymentMethod.data.kind
        : PaymentMethod.SelfPay,
      performerPractitionerId: clinicalProviderId || undefined,
      // A Location a biller saved wins, so their edit sticks to new charges too. Only active charges
      // count, matching the Location the Billing tab shows.
      placeOfService: resolveCapturedPlaceOfService(
        getActiveChargeItems(existingChargeItems.value),
        encounter.value,
      ),
    },
  };
};

/**
 * One conditional create per charge, keyed on the charge's own capture key.
 *
 * Already-charged procedures are skipped using charges read a moment earlier, which leaves a window:
 * two captures of the same encounter can both read nothing and both post. `ifNoneExist` closes that
 * window at the server. A create whose key is already taken comes back as 200 with the existing
 * charge, and nothing is written a second time.
 *
 * A voided charge keeps its key on purpose. Deleting a biller-section row is meant to stick, so
 * re-capturing its procedure — on an amendment's Sign & Lock, say — matches the voided charge and
 * writes nothing instead of bringing back a line a biller took off the encounter. The read counts
 * voided charges for the same reason, which is what keeps that procedure out of the bundle in the
 * first place.
 */
const toConditionalCreateEntry = (resource: CapturedChargeItem): BundleEntry<ChargeItem> => ({
  resource,
  request: {
    method: "POST",
    url: "ChargeItem",
    ifNoneExist: `identifier=${CAPTURED_CHARGE_ITEM_IDENTIFIER_SYSTEM}|${getCapturedChargeItemKey(resource)}`,
  },
});

/**
 * Captures the billing copy of an encounter's procedures: one ChargeItem per Procedure, written in a
 * single transaction so an encounter is never left half charged.
 *
 * Called on Sign & Lock and on an amendment's Sign & Lock, never on a plain Save. A saved encounter
 * is still being edited, and a procedure removed before signing must not leave a charge behind.
 *
 * It reads what the encounter already captured and skips those procedures. Every create it does send
 * is conditional on the charge not already being there. So signing the same encounter twice — even
 * twice at once — adds only what is new, and nothing already charged is removed.
 *
 * The one rewrite is the Location: a charge whose Location was inferred from the visit location
 * moves to the one an amended visit location implies, in a second transaction after the creates. A
 * Location a biller saved is never moved. See {@link resolveCapturedPlaceOfService} and
 * {@link refreshInferredChargeItemsLocation}.
 *
 * Existing charges missing linkage to the practice are linked too, so a charge captured before
 * capture linked charges reaches the biller once its encounter is signed again.
 *
 * @returns The charges this call wrote, empty when there was nothing left to charge.
 */
export const captureEncounterChargeItems = async ({
  medplum,
  clientId,
  encounterId,
  procedures,
  practiceOrganizationId,
}: CaptureEncounterChargeItemsParams): Promise<CaptureEncounterChargeItemsResult> => {
  if (!clientId.trim()) return { ok: false, reason: "missing_client_id" };
  if (!encounterId.trim()) return { ok: false, reason: "missing_encounter_id" };

  const linkagePracticeId = practiceOrganizationId?.trim() || undefined;
  if (!procedures.length) {
    return refreshAndLinkExistingChargeItems(medplum, encounterId, linkagePracticeId);
  }

  const encounterReference = { reference: joinReferenceString("Encounter", encounterId) };

  const context = await readEncounterChargeContext(medplum, clientId, encounterId);
  if (!context.ok) return context;

  const chargeItems = buildEncounterChargeItems({
    procedures,
    existingChargeItems: context.data.existingChargeItems,
    patientReference: { reference: joinReferenceString("Patient", clientId) },
    encounterReference,
    occurrencePeriod: context.data.occurrencePeriod,
    paymentMethod: context.data.paymentMethod,
    practiceOrganizationId,
    performerPractitionerId: context.data.performerPractitionerId,
    placeOfService: context.data.placeOfService?.value,
    isPlaceOfServiceInferred: context.data.placeOfService?.isInferred,
  });

  // Charges whose Location was only inferred from a visit location that has since been amended.
  const refreshedChargeItems = refreshInferredPlaceOfService(
    context.data.existingChargeItems,
    context.data.placeOfService,
  );

  const unlinkedExistingChargeItems = selectChargeItemsMissingPracticeLinkage(
    context.data.existingChargeItems,
    linkagePracticeId,
  );
  const practiceLinkage: PracticeLinkage = {
    encounter: context.data.encounter,
    practiceOrganizationId: linkagePracticeId,
  };

  if (!chargeItems.length) {
    const refresh = await refreshInferredChargeItemsLocation(medplum, refreshedChargeItems);
    return linkChargeItemsToPractice(
      medplum,
      { ok: true, data: [], ...refresh },
      unlinkedExistingChargeItems,
      practiceLinkage,
    );
  }

  let response: Bundle;
  try {
    response = await medplum.executeBatch({
      resourceType: "Bundle",
      type: "transaction",
      entry: chargeItems.map(toConditionalCreateEntry),
    });
  } catch (error) {
    return { ok: false, reason: "write_failed", details: toFailureDetails(error) };
  }

  const written: ChargeItem[] = [];
  const matched: ChargeItem[] = [];

  for (const [index] of chargeItems.entries()) {
    const responseEntry = response.entry?.[index];
    const status = toStatusCode(responseEntry);

    if (status === CREATED_STATUS) {
      let saved: ChargeItem | undefined;
      try {
        saved = await savedChargeItem(medplum, responseEntry);
      } catch (error) {
        return { ok: false, reason: "write_failed", details: toFailureDetails(error) };
      }

      if (!saved) {
        return {
          ok: false,
          reason: "write_failed",
          details: "Transaction succeeded but saved ChargeItem was not returned",
        };
      }

      written.push(saved);
      continue;
    }

    // Another capture wrote this charge between our read and our write. The charge is there, it is
    // just not one we wrote, so it is left out of the result rather than reported as a failure. It is
    // kept for linkage: the match proves the charge exists, not that the other capture linked it.
    // The server may answer a match without the resource, so it resolves through `response.location`
    // the same way a create does. A failed read skips linkage for that charge rather than failing a
    // capture that wrote nothing.
    if (status === MATCHED_STATUS) {
      const matchedResource = await savedChargeItem(medplum, responseEntry).catch(() => undefined);
      if (matchedResource) matched.push(matchedResource);
      continue;
    }

    // A transaction is all or nothing, so any other status means nothing was committed.
    return {
      ok: false,
      reason: "write_failed",
      details: `ChargeItem create returned ${responseEntry?.response?.status ?? "no status"}`,
    };
  }

  const refresh = await refreshInferredChargeItemsLocation(medplum, refreshedChargeItems);

  // Only when this capture wrote something. A charge that is new and unsent puts the encounter in
  // `services-not-sent` whatever it carried before, so the write is always the right one there. A
  // capture that found everything already charged has changed nothing about how far the encounter has
  // got, and writing the code on that path would put it back on an encounter whose Services have all
  // since been sent.
  const chargeItemsToLink = [
    ...written,
    ...unlinkedExistingChargeItems,
    ...selectChargeItemsMissingPracticeLinkage(matched, linkagePracticeId),
  ];

  if (!written.length) {
    return linkChargeItemsToPractice(
      medplum,
      { ok: true, data: written, ...refresh },
      chargeItemsToLink,
      practiceLinkage,
    );
  }

  await afterChargeItemWrites(
    medplum,
    written,
    ChargeItemProvenanceActivityCode.Created,
    practiceOrganizationId,
  );

  // Written after the charges rather than inside their transaction. See tagServicesStatusAfterWrite
  // for why the tag is a separate write and why failing it is not fatal. Created is recorded first so
  // a failed tag still leaves Version History for the charges that landed. The tag goes on whatever
  // linkage does: the charges are written either way, and the next sign writes nothing new, so it
  // would never tag the encounter. A send that already moved these charges on keeps its own tag —
  // see tagCapturedServicesStatus.
  const tagged = await tagCapturedServicesStatus(medplum, encounterId, written);

  return linkChargeItemsToPractice(
    medplum,
    { ...tagged, ...refresh },
    chargeItemsToLink,
    practiceLinkage,
  );
};

/**
 * Brings an encounter's existing charges up to date when it has no procedures to charge. An
 * encounter can keep a charge after its procedures are removed. That charge still has to reach the
 * biller, and its inferred Location still has to follow an amended visit location. Only the
 * encounter and its charges are read, since no charge is built.
 *
 * @param medplum Client that reads the encounter and its charges, moves their Location, and runs
 * `$qh-set-accounts`.
 * @param encounterId Encounter whose charges are updated.
 * @param practiceOrganizationId Trimmed practice to link to. Linkage is skipped without one.
 * @returns An empty success carrying any failed Location move, or the failure of a read or the
 * linkage.
 */
async function refreshAndLinkExistingChargeItems(
  medplum: MedplumClient,
  encounterId: string,
  practiceOrganizationId: string | undefined,
): Promise<CaptureEncounterChargeItemsResult> {
  const [encounter, existingChargeItems] = await Promise.allSettled([
    medplum.readResource("Encounter", encounterId),
    searchEncounterChargeItems(medplum, encounterId),
  ]);

  if (encounter.status === "rejected") {
    return {
      ok: false,
      reason: "encounter_read_failed",
      details: toFailureDetails(encounter.reason),
    };
  }

  if (existingChargeItems.status === "rejected") {
    return {
      ok: false,
      reason: "existing_charges_read_failed",
      details: toFailureDetails(existingChargeItems.reason),
    };
  }

  const refresh = await refreshInferredChargeItemsLocation(
    medplum,
    refreshInferredPlaceOfService(
      existingChargeItems.value,
      resolveCapturedPlaceOfService(
        getActiveChargeItems(existingChargeItems.value),
        encounter.value,
      ),
    ),
  );

  return linkChargeItemsToPractice(
    medplum,
    { ok: true, data: [], ...refresh },
    selectChargeItemsMissingPracticeLinkage(existingChargeItems.value, practiceOrganizationId),
    { encounter: encounter.value, practiceOrganizationId },
  );
}

/**
 * Links charges to the practice so a biller's compartment search can see them. Providers often
 * cannot persist `meta.accounts` on create; `$qh-set-accounts` completes linkage.
 *
 * Links only when the encounter is in the practice's compartment, and otherwise returns `result`
 * untouched. The active practice comes from browser storage that every tab shares, so opening
 * another practice's client in a second tab can change it under this one; linking then would put
 * these charges in front of that practice's billers. A biller can only open an encounter in their own
 * practice's compartment, so skipping leaves out nothing a biller could reach.
 *
 * @param medplum Client that runs `$qh-set-accounts`.
 * @param result The capture's success result, returned when linkage succeeds or is skipped.
 * @param chargeItems Charges to link. Nothing is sent when empty.
 * @param practiceLinkage The encounter and the trimmed practice to link to. Skipped without one.
 * @returns `result`, or `accounts_failed` carrying its charges and any tag or Location failure when
 * linkage throws.
 */
async function linkChargeItemsToPractice(
  medplum: MedplumClient,
  result: CaptureEncounterChargeItemsSuccess,
  chargeItems: ChargeItem[],
  { encounter, practiceOrganizationId }: PracticeLinkage,
): Promise<CaptureEncounterChargeItemsResult> {
  if (!practiceOrganizationId || !chargeItems.length) return result;
  if (!resourceHasOrganizationCompartment(encounter, practiceOrganizationId)) return result;

  const organizationAccounts = [
    { reference: joinReferenceString("Organization", practiceOrganizationId) },
  ];

  try {
    await Promise.all(
      chargeItems.map((chargeItem) =>
        setAccountsBestEffort(medplum, chargeItem, organizationAccounts, {
          extractFromPatient: true,
        }),
      ),
    );
  } catch (error) {
    return {
      ok: false,
      reason: "accounts_failed",
      data: result.data,
      details: toFailureDetails(error),
      ...(result.servicesStatusTagFailure
        ? { servicesStatusTagFailure: result.servicesStatusTagFailure }
        : {}),
      ...(result.placeOfServiceRefreshFailure
        ? { placeOfServiceRefreshFailure: result.placeOfServiceRefreshFailure }
        : {}),
    };
  }

  return result;
}

/**
 * Picks the encounter's existing charges that the practice's compartment search cannot see.
 *
 * @param chargeItems The encounter's existing charges.
 * @param practiceOrganizationId Trimmed practice the charges belong to.
 * @returns Charges missing linkage to that practice. Empty when there is no practice.
 */
function selectChargeItemsMissingPracticeLinkage(
  chargeItems: ChargeItem[],
  practiceOrganizationId: string | undefined,
): ChargeItem[] {
  if (!practiceOrganizationId) return [];

  const practiceReference = {
    reference: joinReferenceString("Organization", practiceOrganizationId),
  };
  return chargeItems.filter(
    (chargeItem) => !resourceHasOrganizationLinkage(chargeItem, practiceReference),
  );
}

type UpdateEncounterChargeItemsLocationParams = {
  medplum: MedplumClient;
  encounterId: string;
  placeOfService: string;
};

// Guarded by a version test so a concurrent edit to the charge (its status, another extension, any
// other field) between the search above and this transaction aborts the whole batch instead of
// being silently overwritten by a stale full-resource PUT.
const toChargeItemLocationPatchEntry = (chargeItem: ChargeItem): BundleEntry<Parameters> => {
  const operations: PatchOperation[] = [
    { op: "test", path: "/meta/versionId", value: chargeItem.meta?.versionId },
    // `add` on an existing member replaces its value per RFC 6902 — `replace` cannot be used here
    // since a ChargeItem with no `extension` yet has no member for `replace` to target, and would
    // fail the whole transaction.
    { op: "add", path: "/extension", value: chargeItem.extension },
  ];

  return {
    resource: {
      resourceType: "Parameters",
      parameter: operations.map(({ op, path, value }) => ({
        name: "operation",
        part: [
          { name: "op", valueCode: op },
          { name: "path", valueString: path },
          ...(value === undefined ? [] : [{ name: "value", valueString: JSON.stringify(value) }]),
        ],
      })),
    },
    // Every charge came from a search of the server, so it always carries an id.
    request: { method: "PATCH", url: joinReferenceString("ChargeItem", chargeItem.id!) },
  };
};

/**
 * Writes each charge's `extension` in one transaction, each PATCH guarded on the version the charge
 * was read at, then records Edited for every charge written.
 *
 * @param medplum Client that runs the transaction and reads the charges back.
 * @param chargeItems The charges as they should be written, each with the version it was read at.
 * @returns Success, or the message of the failed transaction. Nothing is written on failure.
 */
const writeChargeItemsLocation = async (
  medplum: MedplumClient,
  chargeItems: ChargeItem[],
): Promise<{ ok: true } | { ok: false; details: string }> => {
  let response: Bundle;
  try {
    response = await medplum.executeBatch({
      resourceType: "Bundle",
      type: "transaction",
      entry: chargeItems.map(toChargeItemLocationPatchEntry),
    });
  } catch (error) {
    return { ok: false, details: toFailureDetails(error) };
  }

  const patched = await resolvePatchedChargeItems(
    medplum,
    response.entry ?? [],
    chargeItems.length,
  );
  await afterChargeItemWrites(medplum, patched, ChargeItemProvenanceActivityCode.Edited);

  return { ok: true };
};

/**
 * Moves the charges whose inferred Location an amended visit location changed.
 *
 * Runs after the creates, in its own transaction. A biller who edits one of these charges between
 * the read and this write fails its version test, and that costs only the move, never the new
 * charges. The next capture reads the charges again and moves them then.
 *
 * @param medplum Client that writes the charges.
 * @param chargeItems The charges to move, from {@link refreshInferredPlaceOfService}.
 * @returns `placeOfServiceRefreshFailure` when the move failed, to spread onto the capture result.
 */
const refreshInferredChargeItemsLocation = async (
  medplum: MedplumClient,
  chargeItems: ChargeItem[],
): Promise<Pick<CaptureEncounterChargeItemsSuccess, "placeOfServiceRefreshFailure">> => {
  if (!chargeItems.length) return {};

  const result = await writeChargeItemsLocation(medplum, chargeItems);
  return result.ok ? {} : { placeOfServiceRefreshFailure: result.details };
};

/**
 * Sets one Location on every `ChargeItem` an encounter has, voided ones included, in a single
 * transaction.
 *
 * Every charge on the encounter is meant to show the same Location, so a biller editing it from
 * the Billing tab is changing one fact, not one row. Voided charges get it too, since a voided
 * charge is still a record of what happened and where, and leaving it holding a stale value would
 * make it disagree with the rest of the encounter if it were ever un-voided.
 *
 * Never reads or writes the `Encounter` resource. Location lives on the charges themselves —
 * `Encounter.class` is a separate, clinical field this update does not touch.
 */
export const updateEncounterChargeItemsLocation = async ({
  medplum,
  encounterId,
  placeOfService,
}: UpdateEncounterChargeItemsLocationParams): Promise<UpdateEncounterChargeItemsLocationResult> => {
  const trimmedEncounterId = encounterId.trim();
  if (!trimmedEncounterId) return { ok: false, reason: "missing_encounter_id" };

  const trimmedPlaceOfService = placeOfService.trim();
  if (!trimmedPlaceOfService) return { ok: false, reason: "missing_place_of_service" };

  let existingChargeItems: ChargeItem[];
  try {
    existingChargeItems = await searchEncounterChargeItems(medplum, trimmedEncounterId);
  } catch (error) {
    return {
      ok: false,
      reason: "existing_charges_read_failed",
      details: toFailureDetails(error),
    };
  }

  if (!existingChargeItems.length) return { ok: true, data: [] };

  const updatedChargeItems = existingChargeItems.map((chargeItem) => ({
    ...chargeItem,
    extension: replacePlaceOfServiceExtension(chargeItem.extension, trimmedPlaceOfService),
  }));

  const written = await writeChargeItemsLocation(medplum, updatedChargeItems);
  if (!written.ok) return { ok: false, reason: "write_failed", details: written.details };

  return { ok: true, data: updatedChargeItems };
};

/**
 * Voids one captured charge: the soft delete behind removing a row from the biller section.
 *
 * The charge stays on the server. Its status becomes {@link VOIDED_CHARGE_ITEM_STATUS}, which takes
 * it out of the biller section's rows and out of what the encounter will be billed for, while
 * keeping the record of it — and every earlier version of it — in place.
 *
 * Only the charge is touched. The Procedure the charge was captured from and the Condition it was
 * recorded under are the encounter's clinical record, and a biller deciding not to bill for
 * something does not mean it did not happen, so the procedures tab goes on listing both. The write
 * is a patch on that one field for the same reason: nothing else on the charge, and nothing pointing
 * at it, can be caught up in the change. The one exception is the status mirror tag, which the patch
 * clears when a charge that was already sent to billing is voided, so it drops out of the Services
 * tab's Unbilled filter. That is why the charge is read first. See {@link buildChargeItemStatusPatch}.
 *
 * @param chargeItemId The charge to void. Voiding one already voided ends at the same status.
 */
export const voidEncounterChargeItem = async ({
  medplum,
  chargeItemId,
}: {
  medplum: MedplumClient;
  chargeItemId: string;
}): Promise<VoidEncounterChargeItemResult> => {
  if (!chargeItemId.trim()) return { ok: false, reason: "missing_charge_item_id" };

  try {
    const current = await medplum.readResource("ChargeItem", chargeItemId, { cache: "no-cache" });
    const chargeItem = await medplum.patchResource(
      "ChargeItem",
      chargeItemId,
      buildChargeItemStatusPatch(current, VOIDED_CHARGE_ITEM_STATUS),
    );

    return { ok: true, data: chargeItem };
  } catch (error) {
    return { ok: false, reason: "write_failed", details: toFailureDetails(error) };
  }
};
